import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type CSSProperties, type FormEvent } from "react";
import { NavLink, useLocation } from "react-router";
import {
  api,
  ApiError,
  errorText,
  idempotent,
  ifMatch,
  newRequestId,
  type CalendarDto,
  type EventDto,
  type OccurrenceDto,
  type OccurrenceListDto,
} from "../api/client";
import { VoiceButton } from "../capture/VoiceAssistant";
import { calendarPersonIds } from "../calendarAudience";
import { ConnectionBadge } from "../components/ConnectionBadge";
import { EventDetails } from "../components/EventDetails";
import { Icon } from "../components/Icon";
import { useFamilyNow } from "../components/useFamilyNow";
import { useSwipe } from "../components/useSwipe";
import { MonthGrid } from "../components/MonthGrid";
import { PersonWeekGrid } from "../components/PersonWeekGrid";
import { useIsPhone } from "../components/useMedia";
import { WeekAgenda } from "../components/WeekAgenda";
import { addDays, addMonths, datePart, formatLongDate, formatMonthYear, formatShortDate, isoWeek, startOfMonth, startOfWeek, timePart, todayIn, utcRange } from "../format";
import { noteRevision } from "../realtime/realtime";
import { useSession } from "../session";

type CalendarMode = "day" | "week" | "month";

export function CalendarPage() {
  const { me } = useSession();
  const isPhone = useIsPhone();
  const today = useToday(me.family.timeZone);
  const now = useFamilyNow(me.family.timeZone);
  const homeToken = (useLocation().state as { calendarHome?: number } | null)?.calendarHome;
  // Ein Bezugstag für alle Ansichten: Tag, Woche und Monat dieses Tages.
  const [anchor, setAnchor] = useState(today);
  const [editing, setEditing] = useState<EventDto | "new" | null>(null);
  const [selected, setSelected] = useState<OccurrenceDto | null>(null);
  const [compactSchool, setCompactSchool] = useState(() => localStorage.getItem("fp.compactSchool") === "1");
  const [navigation, setNavigation] = useState<{ anchor: string; mode: CalendarMode; picked: string | null; at: number } | null>(null);
  useEffect(() => {
    if (!navigation) return;
    const timer = window.setTimeout(() => setNavigation(null), 6000);
    return () => window.clearTimeout(timer);
  }, [navigation]);
  const [mode, setMode] = useState<CalendarMode>(() => {
    const saved = localStorage.getItem("fp.calendarMode");
    return saved === "day" || saved === "month" ? saved : "week";
  });
  const switchMode = (m: CalendarMode) => {
    setMode(m);
    localStorage.setItem("fp.calendarMode", m);
  };
  const persons = me.persons.filter((p) => p.isActive).map((p) => ({ id: p.id, name: p.displayName, color: p.color }));

  // Sieben Tage ab Bezugstag: „Heute“ steht wirklich zuerst, auch bei einem kurzen, nicht scrollenden Raster.
  const weekStart = anchor;
  const monthStart = startOfMonth(anchor);
  const from = mode === "day" ? anchor : mode === "week" ? weekStart : startOfWeek(monthStart);
  const to = mode === "day" ? addDays(anchor, 1) : mode === "week" ? addDays(weekStart, 7) : addDays(startOfWeek(addDays(addMonths(monthStart, 1), -1)), 7);
  const range = utcRange(from, to);

  const calendars = useQuery({ queryKey: ["calendars"], queryFn: () => api<CalendarDto[]>("/api/v1/calendars") });
  const events = useQuery({
    queryKey: ["events", from, to],
    queryFn: async () => {
      const r = await api<OccurrenceListDto>(`/api/v1/events?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`);
      noteRevision(r.revision);
      return r;
    },
  });

  const open = (o: OccurrenceDto) => setSelected(o);
  const gridEvents = (events.data?.occurrences ?? [])
    .filter((o) => datePart(o.start) < to && (o.allDay && o.end ? datePart(o.end) > from : datePart(o.start) >= from))
    .map((o) => ({
      key: o.occurrenceId,
      title: o.title,
      start: o.start,
      end: o.end,
      allDay: o.allDay,
      location: o.location,
      personIds: calendarPersonIds(o.participants.map((p) => p.personId), me.persons),
      createdByPersonId: o.createdByPersonId,
      source: o.source,
      onSelect: () => void open(o),
    }));

  // Fokustag der Woche: gewählter Tag (aus dem Monat) oder heute; beim Blättern der Wochenanfang.
  const [picked, setPicked] = useState<string | null>(null);
  const [focusToken, setFocusToken] = useState(0);
  const weekEnd = addDays(weekStart, 7);
  const focusDay = picked && picked >= weekStart && picked < weekEnd ? picked : today >= weekStart && today < weekEnd ? today : null;

  const [slide, setSlide] = useState<"" | "from-below" | "from-above">("");
  const lastToday = useRef(today);
  const lastHome = useRef(homeToken);
  useEffect(() => {
    if (lastToday.current !== today || lastHome.current !== homeToken) {
      setAnchor(today);
      setPicked(null);
      setMode("week");
      localStorage.setItem("fp.calendarMode", "week");
      setSlide("");
      setFocusToken((t) => t + 1);
    }
    lastToday.current = today;
    lastHome.current = homeToken;
  }, [today, homeToken]);
  const shift = (n: number) => {
    setNavigation({ anchor, mode, picked, at: Date.now() });
    setSlide(n > 0 ? "from-below" : "from-above");
    setPicked(null);
    setAnchor((a) => (mode === "day" ? addDays(a, n) : mode === "week" ? addDays(a, 7 * n) : addMonths(a, n)));
  };
  // Links/rechts wechseln die Kalenderansicht, kurze senkrechte Gesten den angezeigten Zeitraum.
  const mainRef = useRef<HTMLDivElement>(null);
  useEffect(() => setSlide(""), [mode]);
  const cycleView = (delta: number) => {
    setNavigation({ anchor, mode, picked, at: Date.now() });
    const views: CalendarMode[] = ["day", "week", "month"];
    switchMode(views[(views.indexOf(mode) + delta + views.length) % views.length]);
  };
  useSwipe(mainRef, { onLeft: () => cycleView(1), onRight: () => cycleView(-1),
    onUp: () => shift(1), onDown: () => shift(-1), verticalAtEdges: false });
  const goToday = () => {
    setPicked(null);
    setAnchor(today);
    setFocusToken((t) => t + 1);
  };
  const openDay = (day: string) => {
    setNavigation({ anchor, mode, picked, at: Date.now() });
    setPicked(day);
    setAnchor(day);
    switchMode("day");
  };

  // Tastatur für den Vollbildbetrieb: ← → blättern, T heute, D Tag, W Woche, M Monat, N neuer Termin.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (editing || selected || e.altKey || e.ctrlKey || e.metaKey) return;
      const target = e.target as HTMLElement | null;
      if (target && (target.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))) return;
      const key = e.key.toLowerCase();
      if (e.key === "ArrowLeft") shift(-1);
      else if (e.key === "ArrowRight") shift(1);
      else if (key === "t") goToday();
      else if (key === "d") switchMode("day");
      else if (key === "w") switchMode("week");
      else if (key === "m") switchMode("month");
      else if (key === "n") setEditing("new");
      else return;
      e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const showsToday = mode !== "month" ? anchor === today : monthStart === startOfMonth(today);
  const unit = mode === "day" ? "Tag" : mode === "week" ? "Woche" : "Monat";
  const previousLabel = mode === "day" ? "Voriger Tag" : mode === "week" ? "Vorige Woche" : "Voriger Monat";
  const nextLabel = mode === "day" ? "Nächster Tag" : mode === "week" ? "Nächste Woche" : "Nächster Monat";
  const rangeLabel = mode === "day" ? formatLongDate(anchor) : mode === "week"
    ? `${formatShortDate(weekStart).split(" ")[1]} – ${formatShortDate(addDays(weekStart, 6)).split(" ")[1]}`
    : formatMonthYear(monthStart);
  const feedback = navigation && <div className="navigation-feedback no-swipe" role="status">
    <span>{unit} · {rangeLabel}</span>
    <button className="btn" onClick={() => { setAnchor(navigation.anchor); switchMode(navigation.mode); setPicked(navigation.picked); setNavigation(null); }}>Zurück</button>
  </div>;
  const detail = selected && <EventDetails occurrence={selected}
    canEdit={!!calendars.data?.find(c => c.id === selected.calendarId)?.canWrite}
    onClose={() => setSelected(null)} onEdit={event => { setSelected(null); setEditing(event); }} />;
  const form = editing && (
    <EventForm
      initial={editing === "new" ? null : editing}
      calendars={(calendars.data ?? []).filter((c) => c.canWrite)}
      defaultDate={mode === "day" ? anchor : focusDay ?? anchor}
      onClose={() => setEditing(null)}
    />
  );

  // Handy: volle Fläche für den Kalender, Navigation per Gesten.
  if (isPhone) {
    return (
      <div className={`page calendar-phone view-${mode}`} ref={mainRef}>
        <h1 className="sr-only" aria-live="polite">Kalender – {unit}, {rangeLabel}</h1>
        {events.isError && <p className="error">{errorText(events.error)}</p>}
        {mode === "week" ? (
          <PersonWeekGrid fill persons={persons} events={gridEvents} from={weekStart} today={today} compactSchool={compactSchool}
            focusDay={focusDay} focusToken={focusToken} ready={events.isSuccess}
            corner={<span className="kw"><small>KW</small>{isoWeek(weekStart)}</span>} />
        ) : mode === "day" ? (
          <WeekAgenda persons={persons} events={gridEvents} from={anchor} days={1} today={today} focusDay={anchor} focusToken={focusToken} ready={events.isSuccess} />
        ) : (
          <div className="cal-month">
            <MonthGrid persons={persons} events={gridEvents} monthStart={monthStart} today={today} onOpenDay={openDay} />
          </div>
        )}
        {form}
        {detail}
        {feedback}
      </div>
    );
  }

  return (
    <div className="calendar-full">
      <h1 className="sr-only">Kalender</h1>
      <div className={`calendar-main view-${mode}`} ref={mainRef}>
        {events.isError && <p className="error">{errorText(events.error)}</p>}
        <div key={`${mode}-${from}`} className={`calendar-content ${slide}`}>
        {mode !== "month" ? (
          <PersonWeekGrid
            fill
            compactSchool={compactSchool}
            now={now}
            persons={persons}
            from={mode === "day" ? anchor : weekStart}
            days={mode === "day" ? 1 : 7}
            today={today}
            focusDay={mode === "day" ? anchor : focusDay}
            focusToken={focusToken}
            ready={events.isSuccess}
            corner={mode === "week" ? (
              <span className="kw" title={`Kalenderwoche ${isoWeek(weekStart)}`}>
                <small>KW</small>
                {isoWeek(weekStart)}
              </span>
            ) : <span className="kw"><small>Tag</small>{anchor.slice(8, 10)}</span>}
            events={gridEvents}
          />
        ) : (
          <MonthGrid persons={persons} events={gridEvents} monthStart={monthStart} today={today} onOpenDay={openDay} />
        )}
        </div>
      </div>
      <nav className="calendar-dock" aria-label="Kalendersteuerung">
        <AppMenu />
        <div className="dock-group" role="group" aria-label={`${unit} wählen`}>
          <button className="btn" onClick={() => shift(-1)} aria-label={previousLabel} title={`${previousLabel} (←)`}>‹</button>
          <button className={`btn${showsToday ? " current" : ""}`} onClick={goToday} title="Heute (T)">Heute</button>
          <button className="btn" onClick={() => shift(1)} aria-label={nextLabel} title={`${nextLabel} (→)`}>›</button>
        </div>
        <span className="dock-range" aria-live="polite">{rangeLabel}</span>
        <VoiceButton className="btn dock-voice" />
        <button className="btn primary add" onClick={() => setEditing("new")} aria-label="+ Termin" title="Neuer Termin (N)">+<span className="label"> Termin</span></button>
        <div className="segmented" role="tablist" aria-label="Ansicht">
          <button role="tab" aria-selected={mode === "day"} className={mode === "day" ? "active" : ""} onClick={() => switchMode("day")} title="Tagesansicht (D)">Tag</button>
          <button role="tab" aria-selected={mode === "week"} className={mode === "week" ? "active" : ""} onClick={() => switchMode("week")} title="Wochenansicht (W)">Woche</button>
          <button role="tab" aria-selected={mode === "month"} className={mode === "month" ? "active" : ""} onClick={() => switchMode("month")} title="Monatsansicht (M)">Monat</button>
        </div>
        <FullscreenButton />
        <button className="btn school-toggle" aria-pressed={compactSchool} title="Unveränderten Unterricht je Tag bündeln" onClick={() => {
          setCompactSchool(!compactSchool); localStorage.setItem("fp.compactSchool", compactSchool ? "0" : "1");
        }}>Schule kompakt</button>
        <ConnectionBadge />
      </nav>
      {form}
      {detail}
      {feedback}
    </div>
  );
}

/** Familien-Datum an Minutengrenzen und nach Wiederanzeige prüfen, auch bei gedrosselten Hintergrund-Timern. */
function useToday(timeZone: string): string {
  const [today, setToday] = useState(() => todayIn(timeZone));
  useEffect(() => {
    let timer: number;
    const refresh = () => {
      setToday(todayIn(timeZone));
      window.clearTimeout(timer);
      timer = window.setTimeout(refresh, 60_000 - Date.now() % 60_000);
    };
    refresh();
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, [timeZone]);
  return today;
}

/** Navigation zu den übrigen Bereichen – im Vollbildkalender als Menü unten links statt Tableiste. */
function AppMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: Event) => {
      if (e instanceof KeyboardEvent ? e.key === "Escape" : !ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);
  return (
    <div className="app-menu" ref={ref}>
      <button className="btn" aria-expanded={open} aria-haspopup="menu" aria-label="Menü" title="Menü" onClick={() => setOpen(!open)}>☰</button>
      {open && (
        <div className="app-menu-list" role="menu">
          <NavLink role="menuitem" to="/today"><Icon name="home" /> Heute</NavLink>
          <NavLink role="menuitem" to="/shopping"><Icon name="shopping" /> Einkauf</NavLink>
          <NavLink role="menuitem" to="/inbox"><Icon name="inbox" /> Eingang</NavLink>
          <NavLink role="menuitem" to="/settings"><Icon name="settings" /> Einstellungen</NavLink>
        </div>
      )}
    </div>
  );
}

function FullscreenButton() {
  const [active, setActive] = useState(() => Boolean(document.fullscreenElement));
  useEffect(() => {
    const on = () => setActive(Boolean(document.fullscreenElement));
    document.addEventListener("fullscreenchange", on);
    return () => document.removeEventListener("fullscreenchange", on);
  }, []);
  // iPhone-Safari kennt keine Fullscreen-API für Seiten – dort hilft „Zum Home-Bildschirm“ (PWA).
  if (!document.fullscreenEnabled) return null;
  const toggle = () => void (document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen()).catch(() => undefined);
  return (
    <button className="btn" onClick={toggle} aria-pressed={active} aria-label={active ? "Vollbild beenden" : "Vollbild"} title={active ? "Vollbild beenden" : "Vollbild"}>
      {active ? "⤡" : "⛶"}
    </button>
  );
}

export function EventForm({ initial, calendars, defaultDate, onClose }: { initial: EventDto | null; calendars: CalendarDto[]; defaultDate: string; onClose: () => void }) {
  const { me } = useSession();
  const qc = useQueryClient();
  const [requestId] = useState(() => newRequestId("event"));
  const [title, setTitle] = useState(initial?.title ?? "");
  const [allDay, setAllDay] = useState(initial?.allDay ?? false);
  const [date, setDate] = useState(initial ? datePart(initial.start) : defaultDate);
  const [startTime, setStartTime] = useState(initial && !initial.allDay ? timePart(initial.start) ?? "09:00" : "09:00");
  const [endTime, setEndTime] = useState(initial?.end && !initial.allDay ? timePart(initial.end) ?? "" : "");
  const [endDate, setEndDate] = useState(initial?.end ? initial.allDay ? addDays(initial.end, -1) : datePart(initial.end) !== datePart(initial.start) ? datePart(initial.end) : "" : "");
  const [multiDay, setMultiDay] = useState(!!initial?.end && datePart(initial.end) !== datePart(initial.start));
  const [location, setLocation] = useState(initial?.location ?? "");
  const [description, setDescription] = useState(initial?.description ?? "");
  const [calendarId, setCalendarId] = useState(initial?.calendarId ?? calendars[0]?.id ?? "");
  const [recurrence, setRecurrence] = useState(initial?.recurrence ?? "");
  // Jeder Termin gehört mindestens einer Person; neu: standardmäßig der angemeldete Benutzer.
  const [participants, setParticipants] = useState<string[]>(calendarPersonIds(
    initial?.participants.map((p) => p.personId) ?? (me.personId ? [me.personId] : []), me.persons));
  const [error, setError] = useState<string | null>(null);

  const payload = () => ({
    title,
    allDay,
    start: allDay ? date : `${date}T${startTime}`,
    end: allDay ? (endDate ? addDays(endDate, 1) : null) : endTime ? `${endDate || date}T${endTime}` : null,
    location: location || null,
    description: description || null,
    calendarId: calendarId || null,
    recurrence: recurrence || null,
    participants: participants.map((personId) => ({ personId, role: "attendee" })),
  });

  const save = useMutation({
    mutationFn: async () => {
      if (initial) {
        const body = { ...payload(), clearEnd: payload().end === null, clearRecurrence: !recurrence };
        return api(`/api/v1/events/${initial.id}`, { method: "PATCH", body, headers: ifMatch(initial.version) });
      }
      return api("/api/v1/events", { body: payload(), headers: idempotent(requestId) });
    },
    onSuccess: async () => {
      await qc.invalidateQueries();
      onClose();
    },
    onError: (e) =>
      setError(e instanceof ApiError && (e.code === "version_mismatch" || e.code === "concurrent_modification")
        ? "Der Termin wurde inzwischen von jemand anderem geändert. Bitte schließen und neu öffnen."
        : errorText(e)),
  });

  const cancel = useMutation({
    mutationFn: () => api(`/api/v1/events/${initial!.id}/cancel`, { method: "POST", body: {}, headers: ifMatch(initial!.version) }),
    onSuccess: async () => {
      await qc.invalidateQueries();
      onClose();
    },
    onError: (e) => setError(errorText(e)),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };

  return (
    <div className="dialog-backdrop" role="dialog" aria-modal="true" aria-label={initial ? "Termin bearbeiten" : "Neuer Termin"}>
      <form className="dialog card event-form" onSubmit={submit}>
        <div className="form-bar">
          <button type="button" className="btn ghost" onClick={onClose}>Abbrechen</button>
          <h2>{initial ? "Termin bearbeiten" : "Neuer Termin"}</h2>
          <button className="btn primary" disabled={save.isPending || participants.length === 0} title={participants.length === 0 ? "Bitte mindestens eine Person wählen" : undefined}>
            {save.isPending ? "Speichern …" : "Speichern"}
          </button>
        </div>
        <label>Titel<input value={title} onChange={(e) => setTitle(e.target.value)} required maxLength={200} /></label>
        <fieldset className="person-toggles">
          <legend>Für wen?</legend>
          {me.persons.filter((p) => p.isActive).map((p) => (
            <label key={p.id} className={`person-toggle${participants.includes(p.id) ? " on" : ""}`} style={{ "--pc": p.color } as CSSProperties}>
              <input type="checkbox" checked={participants.includes(p.id)}
                onChange={(e) => setParticipants(e.target.checked
                  ? p.isEveryone ? [p.id] : [...participants.filter((id) => !me.persons.find((person) => person.id === id)?.isEveryone), p.id]
                  : participants.filter((id) => id !== p.id))} />
              <span className="swatch" style={{ background: p.color }} aria-hidden="true" /> {p.displayName}
            </label>
          ))}
        </fieldset>
        {participants.length === 0 && <p className="error small">Bitte mindestens eine Person wählen.</p>}
        <label className="check"><input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} /> ganztägig</label>
        <div className="row">
          <label>Datum<input type="date" value={date} onChange={(e) => setDate(e.target.value)} required /></label>
          {!allDay && <label>Beginn<input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} required /></label>}
        </div>
        {!allDay && <label className="check"><input type="checkbox" checked={multiDay} onChange={e => { setMultiDay(e.target.checked); if (!e.target.checked) setEndDate(""); }} />Endet an einem anderen Tag</label>}
        <div className="row">
          {(allDay || multiDay) && <label>{allDay ? "bis einschließlich" : "Enddatum"}<input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} min={date} /></label>}
          {!allDay && <label>Bis (optional)<input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} /></label>}
        </div>
        {!allDay && !endTime && <p className="hint small">Ohne Ende wird der Termin mit offenem Ende gespeichert – es wird keine Dauer erfunden.</p>}
        <label>Kalender
          <select value={calendarId} onChange={(e) => setCalendarId(e.target.value)}>
            {calendars.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <label>Wiederholung
          <select value={recurrence} onChange={(e) => setRecurrence(e.target.value)}>
            <option value="">keine</option>
            <option value="FREQ=DAILY">täglich</option>
            <option value="FREQ=WEEKLY">wöchentlich</option>
            <option value="FREQ=WEEKLY;INTERVAL=2">alle 2 Wochen</option>
            <option value="FREQ=MONTHLY">monatlich</option>
            <option value="FREQ=YEARLY">jährlich</option>
            {recurrence && !["FREQ=DAILY", "FREQ=WEEKLY", "FREQ=WEEKLY;INTERVAL=2", "FREQ=MONTHLY", "FREQ=YEARLY"].includes(recurrence) && <option value={recurrence}>{recurrence}</option>}
          </select>
        </label>
        <label>Ort<input value={location} onChange={(e) => setLocation(e.target.value)} maxLength={300} /></label>
        <label>Notiz<textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} maxLength={4000} /></label>
        {error && <p className="error">{error}</p>}
        {initial && (
          <div className="row">
            <button type="button" className="btn danger" onClick={() => confirm("Termin wirklich absagen?") && cancel.mutate()}>Termin absagen</button>
          </div>
        )}
      </form>
    </div>
  );
}
